import { useState, useRef, useEffect, useMemo } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Calendar } from "@/components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { ArrowLeft, CalendarIcon, Loader2, CheckCircle, Upload, X, Image } from "lucide-react";
import { format } from "date-fns";
import { toast } from "sonner";
import axios from "axios";
import { usePublicData } from "@/hooks/usePublicData";
import { LOGO_URL, getImageUrl, SITE_ORIGIN, DEFAULT_PHONE } from "@/lib/publicSite";
import { useSeoHeadTags } from "@/hooks/useSeoHeadTags";

const API = `${window.location.origin}/api`;

const defaultServiceOptions = [
  { value: "carpentry", label: "Carpentry" },
  { value: "general-repairs", label: "General Repairs" },
  { value: "furniture-builds", label: "Furniture Builds" }
];

export default function EnquiryPage() {
  const navigate = useNavigate();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);
  const [date, setDate] = useState();
  const [photos, setPhotos] = useState([]);
  const [isUploading, setIsUploading] = useState(false);
  const fileInputRef = useRef(null);
  const [searchParams] = useSearchParams();

  // Shared cached public data — dropdown always matches what's on the site, no flash
  const { bizInfo, services: publicServices } = usePublicData();

  useSeoHeadTags({
    title: "Get a Free Quote | Handyman Abergavenny | Sustainable Handyman Services",
    description: `Request a free quote for handyman services in Abergavenny & Monmouthshire. Carpentry, repairs, furniture builds, fencing & more. Call ${bizInfo?.business_phone || DEFAULT_PHONE} or fill out our form.`,
    canonical: `${SITE_ORIGIN}/enquiry`,
    ogType: "website",
  });
  const serviceOptions = useMemo(() => {
    const items = (publicServices || []).filter((s) => s.title);
    if (!items.length) return defaultServiceOptions;
    return [
      ...items.map((s) => ({ value: s.title, label: s.title })),
      { value: "Something else", label: "Something else" },
    ];
  }, [publicServices]);

  // Track page view on load
  useEffect(() => {
    const trackPageView = async () => {
      try {
        let sessionId = sessionStorage.getItem('shs_session_id');
        if (!sessionId) {
          sessionId = `session_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
          sessionStorage.setItem('shs_session_id', sessionId);
        }
        
        await axios.post(`${API}/analytics/pageview`, null, {
          params: { page: '/enquiry', session_id: sessionId }
        });
      } catch (error) { console.error('Failed to fetch settings:', error); }
    };
    trackPageView();
  }, []);
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    phone: "",
    address: "",
    service: "",
    message: ""
  });

  // Pre-select service from ?service= query param (service landing page CTA)
  useEffect(() => {
    const pre = searchParams.get("service");
    if (!pre) return;
    const match = serviceOptions.find((o) => o.value.toLowerCase() === pre.toLowerCase());
    if (match) setFormData((p) => (p.service ? p : { ...p, service: match.value }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [serviceOptions]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleServiceChange = (value) => {
    setFormData(prev => ({ ...prev, service: value }));
  };

  const handleFileUpload = async (e) => {
    const files = e.target.files;
    if (!files || files.length === 0) return;
    
    setIsUploading(true);
    const newPhotos = [...photos];
    
    for (const file of files) {
      if (newPhotos.length >= 5) {
        toast.error("Maximum 5 photos allowed");
        break;
      }
      try {
        const formData = new FormData();
        formData.append('file', file);
        
        // Try the persistent storage upload endpoint
        let response;
        try {
          response = await axios.post(`${API}/files/upload-public`, formData);
          newPhotos.push(response.data.url);
        } catch (uploadError) {
          // Legacy fallback
          response = await axios.post(`${API}/upload-public-drive`, formData);
          const photoUrl = response.data.drive_url || response.data.url;
          newPhotos.push(photoUrl);
        }
        toast.success(`Uploaded: ${file.name}`);
      } catch (error) {
        toast.error(`Failed to upload: ${file.name}`);
      }
    }
    
    setPhotos(newPhotos);
    setIsUploading(false);
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const removePhoto = (index) => {
    setPhotos(photos.filter((_, i) => i !== index));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.name || !formData.email || !formData.phone || !formData.service || !formData.message) {
      toast.error("Please fill in all required fields");
      return;
    }

    setIsSubmitting(true);

    try {
      const payload = {
        ...formData,
        preferred_date: date ? format(date, "yyyy-MM-dd") : null,
        photos: photos
      };

      await axios.post(`${API}/enquiries`, payload);
      setIsSuccess(true);
      toast.success("Enquiry submitted successfully!");
    } catch (error) {
      console.error("Error submitting enquiry:", error);
      toast.error("Failed to submit enquiry. Please try again.");
    } finally {
      setIsSubmitting(false);
    }
  };

  if (isSuccess) {
    return (
      <div className="min-h-screen bg-background">
        {/* Header */}
        <div className="h-[30vh] bg-primary" />
        
        <div className="max-w-2xl mx-auto px-4 -mt-[15vh]">
          <Card className="shadow-lg" data-testid="success-card">
            <CardContent className="pt-8 pb-8 text-center">
              <div className="bg-accent/10 w-20 h-20 rounded-full flex items-center justify-center mx-auto mb-6">
                <CheckCircle className="h-10 w-10 text-accent" />
              </div>
              <h2 className="text-2xl font-bold font-heading mb-2">Thank You!</h2>
              <p className="text-muted-foreground mb-6" data-testid="enquiry-success-message">
                {bizInfo?.enquiry_success_message || "Your enquiry has been submitted successfully. We'll get back to you within 24-48 hours."}
              </p>
              <p className="text-sm text-muted-foreground mb-6">
                A confirmation email has been sent to <strong>{formData.email}</strong>
              </p>
              <Link to="/" data-testid="back-home-btn">
                <Button className="rounded-full bg-primary hover:bg-primary/90">
                  Back to Home
                </Button>
              </Link>
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      {/* Header Background */}
      {/* Header Background */}
      <div className="h-[30vh] bg-primary relative">
        <div className="absolute top-4 left-4 z-10">
          <Link to="/" className="flex items-center gap-2 text-white/80 hover:text-white transition-colors" data-testid="back-link">
            <ArrowLeft className="h-5 w-5" />
            <span>Back</span>
          </Link>
        </div>
        <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
          <div className="text-center text-white">
            <img src={LOGO_URL} alt="Logo" className="h-16 w-16 rounded-full mx-auto mb-3 bg-white object-cover" />
            <h1 className="text-2xl font-bold font-heading">Request a Quote</h1>
          </div>
        </div>
      </div>

      {/* Form Card */}
      <div className="max-w-2xl mx-auto px-4 -mt-8 sm:-mt-[10vh] pb-12 relative z-10">
        <Card className="shadow-lg" data-testid="enquiry-form-card">
          <CardHeader className="pb-2">
            <CardTitle className="font-heading text-xl">Enquiry Form</CardTitle>
            <CardDescription className="text-sm">Fill out the form below and we&apos;ll get back to you shortly.</CardDescription>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleSubmit} className="space-y-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="name">Full Name *</Label>
                  <Input
                    id="name"
                    name="name"
                    placeholder="John Doe"
                    value={formData.name}
                    onChange={handleInputChange}
                    className="h-12"
                    data-testid="input-name"
                    required
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="email">Email Address *</Label>
                  <Input
                    id="email"
                    name="email"
                    type="email"
                    placeholder="john@example.com"
                    value={formData.email}
                    onChange={handleInputChange}
                    className="h-12"
                    data-testid="input-email"
                    required
                  />
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="phone">Phone Number *</Label>
                  <Input
                    id="phone"
                    name="phone"
                    type="tel"
                    placeholder="07xxx xxxxxx"
                    value={formData.phone}
                    onChange={handleInputChange}
                    className="h-12"
                    data-testid="input-phone"
                    required
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="service-select">Service Required *</Label>
                  <Select onValueChange={handleServiceChange} value={formData.service}>
                    <SelectTrigger id="service-select" className="h-12" data-testid="select-service">
                      <SelectValue placeholder="Select a service" />
                    </SelectTrigger>
                    <SelectContent>
                      {serviceOptions.map(service => (
                        <SelectItem key={service.value} value={service.value} data-testid={`service-option-${service.value.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}>
                          {service.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="address">Your Address</Label>
                <Textarea
                  id="address"
                  name="address"
                  placeholder="Enter your address (for site visit)"
                  value={formData.address}
                  onChange={handleInputChange}
                  className="min-h-[80px] resize-none"
                  data-testid="input-address"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="date-picker-trigger">Preferred Date (Optional)</Label>
                <Popover>
                  <PopoverTrigger asChild>
                    <Button
                      id="date-picker-trigger"
                      variant="outline"
                      className="w-full justify-start text-left font-normal h-12"
                      data-testid="date-picker-trigger"
                    >
                      <CalendarIcon className="mr-2 h-4 w-4" />
                      {date ? format(date, "PPP") : "Select a date"}
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="w-auto p-0" align="start">
                    <Calendar
                      mode="single"
                      selected={date}
                      onSelect={setDate}
                      initialFocus
                      disabled={(date) => date < new Date()}
                      data-testid="calendar"
                    />
                  </PopoverContent>
                </Popover>
              </div>

              <div className="space-y-2">
                <Label htmlFor="message">Project Details *</Label>
                <Textarea
                  id="message"
                  name="message"
                  placeholder="Please describe your project or the work you need done..."
                  value={formData.message}
                  onChange={handleInputChange}
                  className="min-h-[120px]"
                  data-testid="input-message"
                  required
                />
              </div>

              {/* Photo Upload Section */}
              <div className="space-y-2">
                <Label htmlFor="photo-upload-input">Photos (Optional)</Label>
                <p className="text-sm text-muted-foreground">Upload up to 5 photos to help us understand your project</p>
                
                <div 
                  className="border-2 border-dashed border-muted-foreground/30 rounded-lg p-6 text-center hover:border-primary/50 transition-colors cursor-pointer"
                  onClick={() => fileInputRef.current?.click()}
                >
                  <input
                    ref={fileInputRef}
                    id="photo-upload-input"
                    type="file"
                    accept="image/*,.heic,.heif"
                    multiple
                    className="hidden"
                    onChange={handleFileUpload}
                    data-testid="photo-upload-input"
                  />
                  {isUploading ? (
                    <div className="flex flex-col items-center gap-2">
                      <Loader2 className="h-8 w-8 text-primary animate-spin" />
                      <p className="text-sm text-muted-foreground">Uploading...</p>
                    </div>
                  ) : (
                    <div className="flex flex-col items-center gap-2">
                      <Upload className="h-8 w-8 text-muted-foreground/60" />
                      <p className="font-medium">Click to upload photos</p>
                      <p className="text-xs text-muted-foreground">JPEG, PNG, HEIC (iPhone), WebP — max 10MB each</p>
                    </div>
                  )}
                </div>

                {/* Photo Preview Grid */}
                {photos.length > 0 && (
                  <div className="grid grid-cols-3 sm:grid-cols-5 gap-2 mt-3">
                    {photos.map((photo, photoIdx) => (
                      <div key={photo} className="relative group aspect-square rounded-lg overflow-hidden border">
                        <img src={getImageUrl(photo)} alt={`Upload ${photoIdx + 1}`} className="w-full h-full object-cover" />
                        <button
                          type="button"
                          onClick={() => removePhoto(photoIdx)}
                          className="absolute top-1 right-1 bg-red-500 hover:bg-red-600 text-white p-1.5 rounded-full"
                        >
                          <X className="h-3 w-3" />
                        </button>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              <Button
                type="submit"
                className="w-full rounded-full bg-accent hover:bg-accent/90 text-accent-foreground h-12 text-lg font-medium"
                disabled={isSubmitting}
                data-testid="submit-btn"
              >
                {isSubmitting ? (
                  <>
                    <Loader2 className="mr-2 h-5 w-5 animate-spin" />
                    Submitting...
                  </>
                ) : (
                  "Submit Enquiry"
                )}
              </Button>
            </form>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
